import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Drawer } from 'antd';
import { FilterOutlined, PlusOutlined } from '@ant-design/icons';
import {
  useGetServiceRequestInfiniteList,
  UseGetServiceRequestListParams,
  useTagServiceRequest,
  useUntagServiceRequest,
  useGetServiceRequestNotification,
} from 'src/hooks/serviceRequest';
import {
  Table,
  Button,
  SkeletonLoader,
} from '@insight-health-ai/roseate/components';
import {
  getReferralListColumns,
  defaultFilters,
  sortDirectionMap,
} from './constants';

import { PatientDropdown, TagDropdown } from 'src/components';
import { ServiceRequest, ServiceRequestStatus, Tag } from 'src/models';
import { ReferralDetails } from '../details/referralDetails';
import { useScreenSize } from '@insight-health-ai/roseate/utils';
import { CreateReferralModal, CreateDocumentReferralModal } from '../create';
import { SorterResult } from 'antd/es/table/interface';
import { useGetCurrentPractitioner } from 'src/hooks/settings';
import { NotificationHistoryModal } from 'src/pages/referral/list/notificationHistoryModal';
import { ServiceRequestStatusFilter } from 'src/components';
import { ServiceRequestChatStatusFilter } from 'src/components/serviceRequest/serviceRequestChatStatusFilter';

const getDefaultFilters = () => {
  try {
    const filters = new URLSearchParams(window.location.search).get('filters');
    return {
      ...defaultFilters,
      ...(filters ? JSON.parse(filters) : {}),
    };
  } catch (error) {
    return defaultFilters;
  }
};

export const ReferralList = () => {
  const navigate = useNavigate();

  const [idsToPoll, setIdsToPoll] = useState<string[]>([]);
  const [polling, setPolling] = useState<Boolean>(false);

  const { mediaMax } = useScreenSize();

  const [createReferralModal, showCreateReferralModal] = useState(false);
  const [createDocumentReferralModal, showCreateDocumentReferralModal] =
    useState(false);
  const [filters, setFilters] =
    useState<UseGetServiceRequestListParams>(getDefaultFilters);
  const [referralPreview, showReferralPreview] =
    useState<ServiceRequest | null>(null);
  const [notificationHistoryModal, setNotificationHistoryModal] =
    useState(false);
  const [selectedRecord, setSelectedRecord] = useState<ServiceRequest | null>(
    null
  );
  const [notificationData, setNotificationData] = useState<any>(null);

  const { isAdmin, isSuperAdmin } = useGetCurrentPractitioner();

  const {
    hasNextPage,
    data: referralData,
    isLoading: isReferralLoading,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
  } = useGetServiceRequestInfiniteList(filters, {
    refetchInterval: polling ? 3000 : false,
  });

  const { mutateAsync: tagServiceRequest } = useTagServiceRequest();
  const { mutateAsync: untagServiceRequest } = useUntagServiceRequest();
  const {
    mutateAsync: getServiceRequestNotification,
    isPending: isLoadingNotifications,
  } = useGetServiceRequestNotification();

  const applyFilters = (newFilters: Partial<typeof filters>) => {
    const updatedFilters = { ...filters, ...newFilters };
    setFilters(updatedFilters);
    const params = new URLSearchParams();
    params.set('filters', JSON.stringify(updatedFilters));
    navigate(`/referrals?${params.toString()}`);
  };

  const onPreview = (record: ServiceRequest) => {
    showReferralPreview(record);
  };

  const onTagChange = async (
    record: ServiceRequest,
    tag: Tag,
    action: 'add' | 'remove'
  ) => {
    if (action === 'add') {
      await tagServiceRequest({
        serviceRequestId: record.service_request_id,
        tag,
      });
    } else {
      await untagServiceRequest({
        serviceRequestId: record.service_request_id,
        tag,
      });
    }
  };

  const data = useMemo(() => {
    return referralData?.pages.flatMap((page) => page.serviceRequests);
  }, [referralData]);

  useEffect(() => {
    if (
      data?.some(
        (sr) =>
          sr.status?.status_name?.toLowerCase() === 'processing' &&
          idsToPoll.includes(sr.service_request_id)
      )
    ) {
      setPolling(true);
    } else {
      setPolling(false);
    }
  }, [data, idsToPoll]);

  const handleViewNotificationHistory = async (record: ServiceRequest) => {
    try {
      const response = await getServiceRequestNotification({
        serviceRequestId: record.service_request_id,
      });
      setNotificationData(response);
      setSelectedRecord(record);
      setNotificationHistoryModal(true);
    } catch (error) {
      console.error('Error fetching notification history:', error);
    }
  };

  return (
    <>
      <div
        style={{ height: 'calc(100dvh - 100px)' }}
        className="page-wrapper care-connect-page overflow-hidden css-reset"
      >
        <Card
          className="overflow-hidden flex max-h-full flex-col"
          styles={{
            header: {
              paddingInline: '16px',
            },
            title: {
              flexShrink: 0,
            },
            body: {
              padding: 0,
              maxHeight: '100%',
              overflow: 'auto',
              flexDirection: 'column',
            },
          }}
          title={
            <div className="flex items-center flex-wrap gap-3.5 py-2">
              <div className="flex text-sm items-center gap-2">
                <FilterOutlined />
                <span>Filter by:</span>
              </div>
              <PatientDropdown
                onChange={(patientId) => {
                  applyFilters({
                    patientId: patientId ? String(patientId) : undefined,
                  });
                }}
                value={
                  filters.patientId ? Number(filters.patientId) : undefined
                }
              />
              <ServiceRequestStatusFilter
                value={filters.status_ids || []}
                onChange={(status_ids: ServiceRequestStatus['status_id'][]) => {
                  applyFilters({
                    status_ids,
                  });
                }}
              />
              <ServiceRequestChatStatusFilter
                value={filters.chat_completion_status || []}
                onChange={(chat_completion_status: string[]) => {
                  applyFilters({
                    chat_completion_status,
                  });
                }}
              />
              <TagDropdown
                value={filters.tagIds || []}
                onChange={(tagIds) => {
                  applyFilters({ tagIds: tagIds.length ? tagIds : undefined });
                }}
                buttonProps={{
                  variant: 'outlined',
                  size: 'md',
                  appearance: 'secondary',
                  children: 'Tags',
                }}
              />
              <span className="ml-auto right-0">
                <Button
                  onClick={() => {
                    showCreateReferralModal(true);
                  }}
                  icon={<PlusOutlined />}
                  menu={{
                    items: [
                      {
                        label: 'Upload document',
                        key: 'upload-document',
                        onClick: () => {
                          showCreateDocumentReferralModal(true);
                        },
                      },
                    ],
                  }}
                >
                  Add request
                </Button>
              </span>
            </div>
          }
        >
          <Table
            data={data}
            getRowProps={(record) => {
              return {
                className: 'cursor-pointer',
                onDoubleClick: () => {
                  navigate(`/referrals/${record.service_request_id}`);
                },
                onClick: () => {
                  onPreview(record);
                },
              };
            }}
            columns={getReferralListColumns({
              filters,
              onTagChange,
              isAdmin,
              isSuperAdmin,
              onViewNotificationHistory: handleViewNotificationHistory,
            })}
            loading={isReferralLoading}
            pagination={false}
            footer={() => {
              if (!data?.length) {
                return null;
              }

              return (
                <div className="flex flex-col p-3 gap-2 items-center justify-center">
                  {isFetching ? (
                    <SkeletonLoader
                      active
                      paragraph={{ rows: 1, width: 100 }}
                      title={false}
                      style={{ width: 100 }}
                    />
                  ) : (
                    <div className="text-sm">
                      Showing {data?.length} of{' '}
                      {referralData?.pages[0]?.total || 'many'}
                    </div>
                  )}
                  {hasNextPage && (
                    <Button
                      loading={isFetchingNextPage}
                      onClick={() => {
                        fetchNextPage();
                      }}
                    >
                      Show more
                    </Button>
                  )}
                </div>
              );
            }}
            onChange={(_, __, sorter) => {
              const sortConfig = sorter as SorterResult<ServiceRequest>;
              applyFilters({
                sortBy: sortConfig?.columnKey as keyof typeof filters,
                sortDirection:
                  sortDirectionMap[
                    sortConfig?.order as keyof typeof sortDirectionMap
                  ] || undefined,
              });
            }}
          />
        </Card>
      </div>
      {referralPreview && (
        <Drawer
          styles={{
            body: {
              padding: '0.75rem',
            },
          }}
          title="Request details"
          className="insight-background"
          open
          onClose={() => {
            showReferralPreview(null);
          }}
          width={mediaMax('sm') ? '100%' : '90%'}
        >
          <ReferralDetails referralId={referralPreview?.service_request_id} />
        </Drawer>
      )}
      {createReferralModal && (
        <CreateReferralModal
          open
          onClose={() => {
            showCreateReferralModal(false);
          }}
        />
      )}
      {createDocumentReferralModal && (
        <CreateDocumentReferralModal
          open
          onClose={(serviceRequest) => {
            if (
              serviceRequest?.status?.status_name?.toLowerCase() ===
              'processing'
            ) {
              setIdsToPoll((prev) => {
                if (prev.includes(serviceRequest?.service_request_id!)) {
                  return prev;
                }

                return [...prev, serviceRequest?.service_request_id!];
              });
            }
            showCreateDocumentReferralModal(false);
          }}
        />
      )}
      {notificationHistoryModal && selectedRecord && (
        <NotificationHistoryModal
          open={notificationHistoryModal}
          onClose={() => {
            setNotificationHistoryModal(false);
            setSelectedRecord(null);
            setNotificationData(null);
          }}
          record={selectedRecord}
          notificationData={notificationData}
          isLoading={isLoadingNotifications}
        />
      )}
    </>
  );
};
